import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Bot, Clock, Award } from 'lucide-react';

const CourseCatalog = () => {
  const courses = [
    {
      id: 'learn-python-3',
      title: 'Learn Python 3',
      description: 'Master the basics of Python 3.12, one of the most powerful, versatile, and in-demand programming languages today.',
      icon: Code,
      level: 'Beginner',
      duration: '25 hours',
      lessons: 15,
      projects: 5,
      tags: ['Python 3.12', 'Basics', 'AI Assistance', 'Projects', 'Quizzes']
    },
    {
      id: 'build-chatbots',
      title: 'Build Chatbots with Python',
      description: 'Build rules-based and generative AI chatbots with Python. Includes NLP, Data Science, Machine Learning, and more.',
      icon: Bot,
      level: 'Intermediate',
      duration: '45 hours',
      lessons: 25,
      projects: 10,
      tags: ['Python', 'NLP', 'AI', 'Machine Learning', 'Data Science']
    }
  ];
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <section className="bg-white dark:bg-black border-b border-gray-200 dark:border-gray-800 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl md:text-6xl font-black mb-6 text-gray-900 dark:text-white">
            Course <span className="text-highlight">Catalog</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 max-w-2xl">
            Choose from our comprehensive Python courses designed for beginners
          </p>
        </div>
      </section>
      
      {/* Courses Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {courses.map((course) => {
              const Icon = course.icon;
              return (
                <div key={course.id} className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 hover:border-primary-500 transition-all duration-200">
                  {/* Course Header */}
                  <div className="h-56 bg-black dark:bg-gray-950 flex items-center justify-center relative">
                    <Icon size={96} className="text-primary-500" />
                  </div>
                  
                  {/* Course Content */}
                  <div className="p-8">
                    <div className="flex items-center justify-between mb-4">
                      <span className="badge bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                        {course.level}
                      </span>
                      <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm font-medium">
                        <Clock size={16} className="mr-1" />
                        {course.duration}
                      </div>
                    </div>
                    
                    <h3 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{course.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">{course.description}</p>
                    
                    {/* Stats */}
                    <div className="flex gap-6 mb-6 text-sm font-medium">
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Code size={18} className="text-primary-500" />
                        {course.lessons} Lessons
                      </div>
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Award size={18} className="text-primary-500" />
                        {course.projects} Projects
                      </div>
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Bot size={18} className="text-primary-500" />
                        AI Help
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {course.tags.map((tag, index) => (
                        <span key={index} className={index === 0 ? "badge bg-primary-500 text-black" : "badge bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"}>
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    {/* CTA Button */}
                    <Link 
                      to={`/courses/${course.id}`}
                      className="btn-primary w-full text-center inline-block"
                    >
                      View Course
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white tracking-tight">
              What's Included in Every Course
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Comprehensive learning experience with all the tools you need to succeed
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Code className="text-black" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">Interactive Lessons</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Learn by doing with our interactive code editor and instant feedback
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Award className="text-black" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">Real Projects</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Build portfolio-worthy projects that demonstrate your skills
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Bot className="text-black" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900 dark:text-white">AI Assistance</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Get help anytime with Claude AI for explanations and debugging
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseCatalog;




